import type { PlaybookLocale } from '../types'

export const zhPlaybook: PlaybookLocale = {
  hero: {
    tag: 'Playbook · 操作手册',
    title: '真正能落地的那些模式',
    subtitle: '11 段 · 全部来自真实项目',
    leadParagraph:
      'Method 解释为什么 ontology 有效。这一页是它的操作配对——把"怎么干才不踩坑"全部写出来。选一段读、跳着读、踩坑时回来查都行。',
    backToMethodLabel: '← 回到 Method',
  },
  sections: [
    {
      id: 'where-to-start',
      number: '01',
      title: '从哪开始',
      lead: '按这四条标准选第一个场景——两条以上不满足就换。',
      blocks: [
        {
          kind: 'table2col',
          headers: ['标准', '含义'],
          rows: [
            ['规则清晰', '业务边界、决策依据可以显式表达。模糊领域(创意、情感)不适合作为第一站。'],
            ['数据可得', '关键数据已经在 ERP / MES / CRM / 文档里,只是没串起来。'],
            ['闭环可验证', '有可量化指标(响应时长、错误率、合规率),能在 1-2 个月内拿出对比数据。'],
            ['业务方在场', '有一个具体的业务负责人愿意配合定义规则、审核结果、迭代调整。'],
          ],
        },
        {
          kind: 'callout',
          tag: '反模式',
          title: '不要从这里起步',
          body: '从"全企业知识库"起步 · 从"通用 Agent 平台搭一搭"起步 · 从"换更强的模型"起步 · 从"先把数据治理做完"起步。这些做法都会卡在 Demo,跨不过生产门槛。',
        },
        {
          kind: 'bullets',
          bullets: [
            '质量追溯 / 客诉响应——链路清晰、跨部门痛点尖锐、可量化效果显著。',
            '经营分析 / 自然语言问数——高频高价值、决策层愿意付钱。',
            '客户服务——数据天然结构化、可对接业务系统。',
          ],
        },
      ],
    },
    {
      id: 'how-to-model',
      number: '02',
      title: '怎么建本体',
      lead: '三类要素必须同时立。少一个,其他两个都站不稳。',
      blocks: [
        {
          kind: 'table3col',
          headers: ['要素', '是什么', '例子'],
          rows: [
            ['语义元素(Knowledge)', '实体、关系、事件、属性', '客户、订单、设备、批次、工序'],
            ['Action', '组织内可执行的行为——角色 / 属性 / 修改范围', '新建工单、修改预警状态、下采购单'],
            ['Logic', '可执行的业务逻辑——查询 / 工作流 / 自主推理 Agent', '"原料异常 → 反查工单 → 标记成品"'],
          ],
        },
        {
          kind: 'numberedBullets',
          items: [
            {
              title: '概念标准化',
              body: '最常见的失败:一个概念 N 个名字。把每个系统里的别名(material_id / raw_id / 供应商批次号 / 入库编号)列出来,都映射回同一个实体。',
            },
            {
              title: '关系结构化',
              body: '人脑里"很显然有联系"的关系,必须显式表达——原料→成品(投料)、成品→工单(生产)、工序→设备(使用)、供应商→风险等级(评级)、客诉→批次→工序(追溯)。',
            },
            {
              title: '业务规则固化',
              body: '把"资深工程师的判断"变成可计算规则:"信贷 ≥ $1 亿 → VP 委员会"、"湿度 > X% → 某工序易出次品"、"成本超预算 10% → 人工审批"。',
            },
          ],
        },
      ],
    },
    {
      id: 'choose-engine',
      number: '03',
      title: 'Workflow vs Agentic',
      lead: '一种引擎不够——一个 prompt 就更不够。',
      blocks: [
        {
          kind: 'table2col',
          headers: ['任务特征', '建议'],
          rows: [
            ['路径已知、需要审计、过程可控', 'Workflow'],
            ['每步条件已知 / 配置驱动', 'Workflow'],
            ['多次调整都不影响整体流程', 'Workflow'],
            ['任务长且不确定、需要现场规划', 'Agentic'],
            ['多步推理、目标驱动', 'Agentic'],
            ['探索性问题、归因分析、What-if', 'Agentic'],
          ],
        },
        {
          kind: 'callout',
          tag: '实际情况',
          title: '一个场景里它们通常混合',
          body: '质量追溯:追溯本身(原料 → 工序 → 成品)是 Workflow——链路固定;归因分析是 Agentic——路径未知;改进方案是 Agentic;整改工单派发又是 Workflow。',
        },
      ],
    },
    {
      id: 'behavior-control',
      number: '04',
      title: '怎么管 Agent 行为',
      lead: '业务硬约束 = Agent 硬约束。不是"请你不要幻觉",是"这条规则你违反不了"。',
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            '在 **ontology** 里定义约束,不在 prompt 里。"采购 > 100 万必须 VP 审批"是硬规则,只编码一次。',
            'Agent 生成的方案必须**通过本体校验**。违反触发重推。',
            '校验过程**可视化**——业务方能看到"为什么被打回"。',
          ],
        },
        {
          kind: 'callout',
          tag: '冲突解决',
          title: '如果 Agent 推断和本体冲突?',
          body: '本体覆盖范围内 → 硬约束否决,Agent 重推。未覆盖领域 → 发挥大模型灵活推理,但标注"未经验证"。',
        },
        {
          kind: 'numberedBullets',
          items: [
            {
              title: '标准操作',
              body: 'BOM 录入、常规工艺调整——Agent 自主完成,日志写入审计模块。',
            },
            {
              title: '超阈值决策',
              body: '成本超预算 10%、合同金额超 VP 阈值——触发人工审批,系统推送决策辅助报告。',
            },
            {
              title: '可推翻权',
              body: '员工保留推翻 Agent 决策的权力。确保合理性和有效性。',
            },
          ],
        },
      ],
    },
    {
      id: 'traceability',
      number: '05',
      title: '可追溯是天生的',
      lead: '审计就绪不是事后改,是架构属性。',
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            '每一步推理都能链回**规则**(本体中定义)。',
            '每一个数据点都能链回**源字段**。',
            '每一个动作都能链回执行它的 **Agent / Workflow**。',
            '每一次触发都能链回**时间 + 触发用户/事件**。',
          ],
        },
        {
          kind: 'callout',
          tag: '具体例子',
          title: '一次质量追溯链路',
          body: '原料批次 P-2104 → 入库检验 → 工单 W-781, W-782 → 设备 R-031, R-033 → 工序(焊接、装配) → 成品批次 F-901..F-985 → 出库 + 客户 C-12, C-15, C-44。任意一段断了,系统能告诉你哪一段断了、为什么断、怎么补。',
        },
      ],
    },
    {
      id: 'cold-start',
      number: '06',
      title: '冷启动 —— 从数周到小时',
      lead: "不能等\"完美本体\"才开始用。",
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            '对结构化明显的任务,自动归纳。',
            '置信度驱动路由:低置信任务进入人工审核队列。',
            '人工反馈持续优化自动归纳能力。',
            '领域本体库(~3,600 概念,~8,400 关系)作为起点脚手架。',
          ],
        },
        {
          kind: 'table2col',
          headers: ['场景类型', '周期'],
          rows: [
            ['业务验证', '1-2 周'],
            ['通用场景', '1 个月内'],
            ['高复杂度场景', '1-6 个月'],
          ],
        },
      ],
    },
    {
      id: 'deployment',
      number: '07',
      title: '按数据敏感度选部署',
      blocks: [
        {
          kind: 'table2col',
          headers: ['模式', '适用'],
          rows: [
            ['本地化', '金融 / 政务 / 医疗 / 强监管制造——大部分客户'],
            ['云端', '中小微 / 低敏感度场景 / 快速迭代'],
            ['混合', '不同业务单元敏感度不同——推荐路径'],
          ],
        },
      ],
    },
    {
      id: 'coherence',
      number: '08',
      title: '避免"AI 越多越分散"',
      lead: '应用数量在增加,但智能依然碎片化。这是要避免的失败模式。',
      blocks: [
        {
          kind: 'bullets',
          bullets: [
            '反模式:每个新场景都从头来 · 每个部门发明自己的逻辑 · 新工具上线时没做语义对齐。',
            '一致性来源:统一的领域本体 · 可复用的 Onto-Skills(质量追溯、任务派发作为标准能力) · 认知能力反哺到本体里。',
          ],
        },
        {
          kind: 'callout',
          tag: '怎么测一致性',
          body: '同一个概念在所有 Agent 里含义一致 · 一份新规则只改一次(在本体里) · 同一份数据所有消费者看到的结构相同。',
        },
      ],
    },
    {
      id: 'quality',
      number: '09',
      title: '怎么验证"AI 真的懂了"',
      lead: '别看回答流畅,看这四个维度。',
      blocks: [
        {
          kind: 'table3col',
          headers: ['维度', '看起来在工作', '真的在参与业务'],
          rows: [
            ['回答 vs 推理', '拉数据、算结果、做总结', '也能回答"为什么"、"是不是异常"'],
            ['接口 vs 流程', '接进了接口', '关键判断从人转移到系统'],
            ['解决 vs 沉淀', '一次性应答', '数据反哺,系统越用越聪明'],
            ['执行 vs 决策', '完成动作', '在决策点提供判断路径 + 依据'],
          ],
        },
      ],
    },
    {
      id: 'anti-patterns',
      number: '10',
      title: '反模式合集',
      blocks: [
        {
          kind: 'table2col',
          headers: ['反模式', '应该做的'],
          rows: [
            ['先建知识图谱,再考虑业务', '先建场景本体,KG 只是其中一层'],
            ['一个 Prompt 解决所有问题', 'Workflow + Agentic 双引擎,按任务选'],
            ['所有数据先治理完再开始', '本体本身就是治理'],
            ['Agent 自动跑,出问题再说', '业务硬约束写在本体里,违反就回滚'],
            ['模型越大越好', '业务语义越准越好'],
            ['接进系统就算落地', '关键判断从人转移到系统才算落地'],
            ['每个场景重新搭一套', '统一领域本体,场景之间复用'],
            ['模板越多越万能', '模板越多越容易语义分裂'],
          ],
        },
      ],
    },
    {
      id: 'readiness',
      number: '11',
      title: '准备度自查表',
      lead: '开工前先回答这九个。九个都能答 = 可以开工。三个以上答不出 = 回到 §01。',
      blocks: [
        {
          kind: 'checklist',
          items: [
            '能说出核心业务对象有哪些吗?(客户、订单、设备……)',
            '这些对象之间的关系能不能画出来?',
            '哪些是硬规则(必须遵守),哪些是软经验(可以推翻)?',
            '哪些动作 Agent 可以自主执行,哪些必须人工审批?',
            '有没有一个业务负责人愿意配合定义规则、审核结果?',
            '第一个场景能不能在 1-2 个月内拿出对比数据?',
            '数据敏感度是什么级别?(决定部署模式)',
            '出了问题,审计要追溯到什么粒度?',
            '这个场景如果成功,下一步往哪扩?',
          ],
        },
      ],
    },
  ],
}
